/**
 * PremiumDashboard.jsx
 * Dashboard widgets with KPIs and metrics
 *
 * Design System Premium Fleet v2.0
 * Inspired by Samsara, Linear App, Fleetio
 */

import React, { useMemo, useCallback, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Skeleton,
  LinearProgress,
  Tooltip,
  alpha,
  useTheme,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  TrendingFlat as TrendingFlatIcon,
  MoreVert as MoreVertIcon,
  Refresh as RefreshIcon,
  OpenInNew as OpenInNewIcon,
  ChevronRight as ChevronRightIcon,
  Circle as CircleIcon,
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  ResponsiveContainer,
} from 'recharts';
import { usePremiumTokens } from '../../theme/usePremiumTokens';
import { useTranslation } from '../LocalizationProvider';

// ============================================================================
// TREND BADGE - Up/Down Indicator
// ============================================================================

export const TrendBadge = ({
  value,
  direction = 'flat', // 'up', 'down', 'flat'
  size = 'sm',
  showIcon = true,
  suffix = '%',
}) => {
  const tokens = usePremiumTokens();

  const colors = {
    up: tokens?.semantic?.success || '#22c55e',
    down: tokens?.semantic?.error || '#ef4444',
    flat: tokens?.text?.tertiary || '#6b7280',
  };

  const bgColors = {
    up: alpha(colors.up, 0.1),
    down: alpha(colors.down, 0.1),
    flat: alpha(colors.flat, 0.1),
  };

  const icons = {
    up: <TrendingUpIcon sx={{ fontSize: size === 'sm' ? 14 : 18 }} />,
    down: <TrendingDownIcon sx={{ fontSize: size === 'sm' ? 14 : 18 }} />,
    flat: <TrendingFlatIcon sx={{ fontSize: size === 'sm' ? 14 : 18 }} />,
  };

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        px: size === 'sm' ? 1 : 1.5,
        py: size === 'sm' ? 0.25 : 0.5,
        borderRadius: tokens?.radius?.full || '9999px',
        fontSize: size === 'sm' ? 12 : 14,
        fontWeight: 500,
        bgcolor: bgColors[direction],
        color: colors[direction],
      }}
    >
      {showIcon && icons[direction]}
      {direction === 'up' && '+'}
      {value}{suffix}
    </Box>
  );
};

// ============================================================================
// MINI CHART - Sparkline inside cards
// ============================================================================

export const MiniChart = ({
  data = [],
  type = 'line', // 'line', 'area', 'bar'
  color,
  width = '100%',
  height = 40,
  showDots = false,
}) => {
  const tokens = usePremiumTokens();
  const chartColor = color || tokens?.chart?.series?.[0] || '#6366f1';

  if (!data || data.length === 0) {
    return (
      <Box sx={{ width, height, bgcolor: alpha(chartColor, 0.1), borderRadius: 1 }} />
    );
  }

  const chartData = data.map((value, index) => ({ value, index }));

  const renderChart = () => {
    switch (type) {
      case 'area':
        return (
          <AreaChart data={chartData}>
            <defs>
              <linearGradient id={`miniGrad-${chartColor}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={chartColor} stopOpacity={0.3} />
                <stop offset="100%" stopColor={chartColor} stopOpacity={0} />
              </linearGradient>
            </defs>
            <Area
              type="monotone"
              dataKey="value"
              stroke={chartColor}
              strokeWidth={2}
              fill={`url(#miniGrad-${chartColor})`}
              dot={false}
            />
          </AreaChart>
        );
      case 'bar':
        return (
          <BarChart data={chartData}>
            <Bar
              dataKey="value"
              fill={chartColor}
              radius={[2, 2, 0, 0]}
            />
          </BarChart>
        );
      default: // line
        return (
          <LineChart data={chartData}>
            <Line
              type="monotone"
              dataKey="value"
              stroke={chartColor}
              strokeWidth={2}
              dot={showDots ? { fill: chartColor, r: 2 } : false}
            />
          </LineChart>
        );
    }
  };

  return (
    <Box sx={{ width, height }}>
      <ResponsiveContainer width="100%" height="100%">
        {renderChart()}
      </ResponsiveContainer>
    </Box>
  );
};

// ============================================================================
// STAT CARD - Individual KPI Card
// ============================================================================

export const StatCard = ({
  title,
  value,
  icon,
  trend,
  color = 'primary', // 'primary', 'success', 'warning', 'error', 'info'
  subtitle,
  loading = false,
  onClick,
  miniChart,
  size = 'md', // 'sm', 'md', 'lg'
  showMenu = false,
  onMenuClick,
}) => {
  const tokens = usePremiumTokens();
  const t = useTranslation();

  // Cores que funcionam em ambos os modos
  const isDark = tokens?.isDark !== false; // default to dark if undefined
  const surfaceBg = tokens?.surface?.elevated || (isDark ? '#161b22' : '#ffffff');
  const borderSubtle = tokens?.border?.subtle || (isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.08)');
  const borderDefault = tokens?.border?.default || (isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.15)');
  const textPrimary = tokens?.text?.primary || (isDark ? '#f0f6fc' : '#1f2328');
  const textSecondary = tokens?.text?.secondary || (isDark ? '#8b949e' : '#656d76');
  const textTertiary = tokens?.text?.tertiary || (isDark ? '#6e7681' : '#8b949e');

  const colorMap = {
    primary: tokens?.accent?.primary || (isDark ? '#00d4aa' : '#00a884'),
    success: tokens?.semantic?.success || (isDark ? '#00d4aa' : '#00a884'),
    warning: tokens?.semantic?.warning || (isDark ? '#d29922' : '#bf8700'),
    error: tokens?.semantic?.error || (isDark ? '#f85149' : '#cf222e'),
    info: tokens?.semantic?.info || (isDark ? '#58a6ff' : '#0969da'),
  };

  const selectedColor = colorMap[color] || colorMap.primary;

  const sizeStyles = {
    sm: { p: 2, titleSize: 12, valueSize: 20, iconSize: 32 },
    md: { p: 3, titleSize: 13, valueSize: 28, iconSize: 40 },
    lg: { p: 4, titleSize: 14, valueSize: 36, iconSize: 48 },
  };

  const styles = sizeStyles[size] || sizeStyles.md;

  if (loading) {
    return (
      <Paper
        sx={{
          p: styles.p,
          bgcolor: surfaceBg,
          borderRadius: tokens?.radius?.lg || 12,
          border: `1px solid ${borderSubtle}`,
        }}
      >
        <Skeleton variant="text" width={80} height={20} />
        <Skeleton variant="text" width={120} height={40} sx={{ mt: 1 }} />
        <Skeleton variant="text" width={60} height={16} sx={{ mt: 1 }} />
      </Paper>
    );
  }

  return (
    <Paper
      onClick={onClick}
      sx={{
        p: styles.p,
        bgcolor: surfaceBg,
        borderRadius: tokens?.radius?.lg || 12,
        border: `1px solid ${borderSubtle}`,
        transition: 'all 150ms ease',
        cursor: onClick ? 'pointer' : 'default',
        position: 'relative',
        overflow: 'hidden',
        '&:hover': onClick ? {
          borderColor: borderDefault,
          boxShadow: tokens?.shadows?.md || '0 4px 6px -1px rgba(0,0,0,0.1)',
          transform: 'translateY(-2px)',
        } : {},
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {icon && (
            <Box
              sx={{
                width: styles.iconSize,
                height: styles.iconSize,
                borderRadius: tokens?.radius?.md || 8,
                bgcolor: alpha(selectedColor, 0.1),
                color: selectedColor,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                '& svg': { fontSize: styles.iconSize * 0.5 },
              }}
            >
              {icon}
            </Box>
          )}
          <Typography
            sx={{
              fontSize: styles.titleSize,
              fontWeight: 500,
              color: textSecondary,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              background: 'transparent',
            }}
          >
            {title}
          </Typography>
        </Box>
        {showMenu && (
          <IconButton size="small" onClick={onMenuClick} sx={{ color: tokens?.text?.tertiary }}>
            <MoreVertIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      {/* Value */}
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2, mb: miniChart ? 2 : 0 }}>
        <Typography
          sx={{
            fontSize: styles.valueSize,
            fontWeight: 600,
            color: textPrimary,
            lineHeight: 1,
            background: 'transparent',
          }}
        >
          {typeof value === 'number' ? value.toLocaleString() : value}
        </Typography>
        {trend && (
          <TrendBadge
            value={trend.value}
            direction={trend.direction}
            size={size === 'lg' ? 'md' : 'sm'}
          />
        )}
      </Box>

      {/* Mini Chart */}
      {miniChart && (
        <Box sx={{ mt: 2 }}>
          <MiniChart
            data={miniChart.data}
            type={miniChart.type}
            color={miniChart.color || selectedColor}
            height={miniChart.height || 40}
          />
        </Box>
      )}

      {/* Subtitle */}
      {subtitle && (
        <Typography
          sx={{
            mt: 1,
            fontSize: 12,
            color: textTertiary,
            background: 'transparent',
          }}
        >
          {subtitle}
        </Typography>
      )}

      {/* Click indicator */}
      {onClick && (
        <ChevronRightIcon
          sx={{
            position: 'absolute',
            bottom: styles.p * 8,
            right: styles.p * 8,
            fontSize: 20,
            color: tokens?.text?.tertiary || '#6b7280',
            opacity: 0.5,
          }}
        />
      )}
    </Paper>
  );
};

// ============================================================================
// STAT CARD GRID - Grid of KPIs
// ============================================================================

export const StatCardGrid = ({
  children,
  columns = 4,
  gap = 3,
  responsive = true,
}) => {
  const gridTemplateColumns = responsive
    ? {
        xs: 'repeat(1, 1fr)',
        sm: 'repeat(2, 1fr)',
        md: `repeat(${Math.min(columns, 3)}, 1fr)`,
        lg: `repeat(${columns}, 1fr)`,
      }
    : `repeat(${columns}, 1fr)`;

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns,
        gap,
      }}
    >
      {children}
    </Box>
  );
};

// ============================================================================
// PROGRESS CARD - Progress/Goal
// ============================================================================

export const ProgressCard = ({
  title,
  current,
  target,
  unit = '',
  color = 'primary',
  showPercentage = true,
  icon,
  loading = false,
}) => {
  const tokens = usePremiumTokens();
  const t = useTranslation();

  const colorMap = {
    primary: tokens?.brand?.primary || '#6366f1',
    success: tokens?.semantic?.success || '#22c55e',
    warning: tokens?.semantic?.warning || '#f59e0b',
    error: tokens?.semantic?.error || '#ef4444',
  };

  const selectedColor = colorMap[color] || colorMap.primary;
  const percentage = target > 0 ? Math.min((current / target) * 100, 100) : 0;

  if (loading) {
    return (
      <Paper
        sx={{
          p: 3,
          bgcolor: tokens?.surface?.secondary || '#1f2937',
          borderRadius: tokens?.radius?.lg || 12,
          border: `1px solid ${tokens?.border?.subtle || '#374151'}`,
        }}
      >
        <Skeleton variant="text" width={100} height={20} />
        <Skeleton variant="rectangular" height={8} sx={{ mt: 2, borderRadius: 1 }} />
        <Skeleton variant="text" width={80} height={16} sx={{ mt: 1 }} />
      </Paper>
    );
  }

  return (
    <Paper
      sx={{
        p: 3,
        bgcolor: tokens?.surface?.secondary || '#1f2937',
        borderRadius: tokens?.radius?.lg || 12,
        border: `1px solid ${tokens?.border?.subtle || '#374151'}`,
        transition: 'all 150ms ease',
        '&:hover': {
          borderColor: tokens?.border?.default || '#4b5563',
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {icon && (
            <Box sx={{ color: selectedColor }}>
              {icon}
            </Box>
          )}
          <Typography
            sx={{
              fontSize: 13,
              fontWeight: 500,
              color: tokens?.text?.secondary || '#9ca3af',
            }}
          >
            {title}
          </Typography>
        </Box>
        {showPercentage && (
          <Typography
            sx={{
              fontSize: 14,
              fontWeight: 600,
              color: selectedColor,
            }}
          >
            {percentage.toFixed(0)}%
          </Typography>
        )}
      </Box>

      <LinearProgress
        variant="determinate"
        value={percentage}
        sx={{
          height: 8,
          borderRadius: tokens?.radius?.full || 9999,
          bgcolor: alpha(selectedColor, 0.15),
          '& .MuiLinearProgress-bar': {
            borderRadius: tokens?.radius?.full || 9999,
            bgcolor: selectedColor,
          },
        }}
      />

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1.5 }}>
        <Typography sx={{ fontSize: 12, color: tokens?.text?.tertiary || '#6b7280' }}>
          {current.toLocaleString()} {unit}
        </Typography>
        <Typography sx={{ fontSize: 12, color: tokens?.text?.tertiary || '#6b7280' }}>
          {target.toLocaleString()} {unit}
        </Typography>
      </Box>
    </Paper>
  );
};

// ============================================================================
// STATUS DISTRIBUTION - Status bar/donut/pills
// ============================================================================

export const StatusDistribution = ({
  data = [],
  variant = 'bar', // 'bar', 'pills', 'legend'
  showLegend = true,
  showPercentage = true,
  showCount = true,
  total,
  loading = false,
}) => {
  const tokens = usePremiumTokens();
  const t = useTranslation();

  // Cores que funcionam em ambos os modos
  const isDark = tokens?.isDark !== false;
  const surfaceBg = tokens?.surface?.elevated || (isDark ? '#161b22' : '#ffffff');
  const borderSubtle = tokens?.border?.subtle || (isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.08)');
  const surfaceTertiary = tokens?.surface?.tertiary || (isDark ? '#21262d' : '#f6f8fa');
  const textPrimary = tokens?.text?.primary || (isDark ? '#f0f6fc' : '#1f2328');
  const textSecondary = tokens?.text?.secondary || (isDark ? '#8b949e' : '#656d76');

  const calculatedTotal = total || data.reduce((acc, item) => acc + (item.count || 0), 0);

  if (loading) {
    return (
      <Paper
        sx={{
          p: 3,
          bgcolor: surfaceBg,
          borderRadius: tokens?.radius?.lg || 12,
          border: `1px solid ${borderSubtle}`,
        }}
      >
        <Skeleton variant="text" width={150} height={20} />
        <Skeleton variant="rectangular" height={24} sx={{ mt: 2, borderRadius: 1 }} />
        <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
          <Skeleton variant="text" width={60} height={16} />
          <Skeleton variant="text" width={60} height={16} />
          <Skeleton variant="text" width={60} height={16} />
        </Box>
      </Paper>
    );
  }

  const renderBar = () => (
    <Box
      sx={{
        display: 'flex',
        height: 24,
        borderRadius: tokens?.radius?.md || 8,
        overflow: 'hidden',
        bgcolor: surfaceTertiary,
      }}
    >
      {data.map((item, index) => {
        const percentage = calculatedTotal > 0 ? (item.count / calculatedTotal) * 100 : 0;
        return (
          <Tooltip
            key={item.status || index}
            title={`${item.label || item.status}: ${item.count.toLocaleString()} (${percentage.toFixed(1)}%)`}
          >
            <Box
              sx={{
                width: `${percentage}%`,
                bgcolor: item.color,
                transition: 'width 300ms ease',
                '&:hover': {
                  opacity: 0.8,
                },
              }}
            />
          </Tooltip>
        );
      })}
    </Box>
  );

  const renderPills = () => (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
      {data.map((item, index) => {
        const percentage = calculatedTotal > 0 ? (item.count / calculatedTotal) * 100 : 0;
        return (
          <Box
            key={item.status || index}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 2,
              py: 1,
              borderRadius: tokens?.radius?.full || 9999,
              bgcolor: alpha(item.color, 0.15),
              color: item.color,
            }}
          >
            <CircleIcon sx={{ fontSize: 8 }} />
            <Typography sx={{ fontSize: 13, fontWeight: 500, background: 'transparent' }}>
              {item.label || item.status}
            </Typography>
            {showCount && (
              <Typography sx={{ fontSize: 13, fontWeight: 600, background: 'transparent' }}>
                {item.count.toLocaleString()}
              </Typography>
            )}
            {showPercentage && (
              <Typography sx={{ fontSize: 12, opacity: 0.8, background: 'transparent' }}>
                ({percentage.toFixed(0)}%)
              </Typography>
            )}
          </Box>
        );
      })}
    </Box>
  );

  const renderLegend = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {data.map((item, index) => {
        const percentage = calculatedTotal > 0 ? (item.count / calculatedTotal) * 100 : 0;
        return (
          <Box
            key={item.status || index}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  bgcolor: item.color,
                }}
              />
              <Typography sx={{ fontSize: 14, color: textPrimary, background: 'transparent' }}>
                {item.label || item.status}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {showCount && (
                <Typography sx={{ fontSize: 14, fontWeight: 600, color: textPrimary, background: 'transparent' }}>
                  {item.count.toLocaleString()}
                </Typography>
              )}
              {showPercentage && (
                <Typography
                  sx={{
                    fontSize: 13,
                    color: textSecondary,
                    minWidth: 45,
                    textAlign: 'right',
                    background: 'transparent',
                  }}
                >
                  {percentage.toFixed(0)}%
                </Typography>
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );

  return (
    <Box>
      {variant === 'bar' && (
        <>
          {renderBar()}
          {showLegend && (
            <Box sx={{ mt: 2 }}>
              {renderLegend()}
            </Box>
          )}
        </>
      )}
      {variant === 'pills' && renderPills()}
      {variant === 'legend' && renderLegend()}
    </Box>
  );
};

// ============================================================================
// ALERTS WIDGET - Recent alerts
// ============================================================================

export const AlertsWidget = ({
  alerts = [],
  maxItems = 5,
  loading = false,
  onViewAll,
  onAlertClick,
  emptyMessage,
}) => {
  const tokens = usePremiumTokens();
  const t = useTranslation();

  const displayAlerts = alerts.slice(0, maxItems);

  if (loading) {
    return (
      <Paper
        sx={{
          p: 3,
          bgcolor: tokens?.surface?.secondary || '#1f2937',
          borderRadius: tokens?.radius?.lg || 12,
          border: `1px solid ${tokens?.border?.subtle || '#374151'}`,
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Skeleton variant="text" width={120} height={24} />
          <Skeleton variant="text" width={60} height={24} />
        </Box>
        {[...Array(3)].map((_, i) => (
          <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Skeleton variant="circular" width={32} height={32} />
            <Box sx={{ flex: 1 }}>
              <Skeleton variant="text" width="80%" height={16} />
              <Skeleton variant="text" width="40%" height={14} />
            </Box>
          </Box>
        ))}
      </Paper>
    );
  }

  return (
    <Paper
      sx={{
        p: 3,
        bgcolor: tokens?.surface?.secondary || '#1f2937',
        borderRadius: tokens?.radius?.lg || 12,
        border: `1px solid ${tokens?.border?.subtle || '#374151'}`,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography
          sx={{
            fontSize: 15,
            fontWeight: 600,
            color: tokens?.text?.primary || '#f9fafb',
          }}
        >
          {t('dashboardRecentEvents')}
        </Typography>
        {onViewAll && (
          <Typography
            onClick={onViewAll}
            sx={{
              fontSize: 13,
              color: tokens?.brand?.primary || '#6366f1',
              cursor: 'pointer',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            {t('dashboardViewAll')}
          </Typography>
        )}
      </Box>

      {/* Alerts list */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {displayAlerts.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              minHeight: 100,
            }}
          >
            <Typography sx={{ fontSize: 14, color: tokens?.text?.tertiary || '#6b7280' }}>
              {emptyMessage || t('dashboardNoData')}
            </Typography>
          </Box>
        ) : (
          displayAlerts.map((alert, index) => (
            <Box
              key={alert.id || index}
              onClick={() => onAlertClick?.(alert)}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 2,
                p: 1.5,
                mx: -1.5,
                borderRadius: tokens?.radius?.md || 8,
                cursor: onAlertClick ? 'pointer' : 'default',
                transition: 'background 150ms ease',
                '&:hover': onAlertClick ? {
                  bgcolor: tokens?.surface?.hover || alpha('#fff', 0.05),
                } : {},
                borderBottom: index < displayAlerts.length - 1
                  ? `1px solid ${tokens?.border?.subtle || '#374151'}`
                  : 'none',
              }}
            >
              {/* Alert icon/indicator */}
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: alert.color || tokens?.semantic?.warning || '#f59e0b',
                  mt: 0.75,
                  flexShrink: 0,
                }}
              />

              {/* Content */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: 14,
                    color: tokens?.text?.primary || '#f9fafb',
                    lineHeight: 1.4,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {alert.message || alert.title}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                  {alert.device && (
                    <Typography sx={{ fontSize: 12, color: tokens?.text?.secondary || '#9ca3af' }}>
                      {alert.device}
                    </Typography>
                  )}
                  <Typography sx={{ fontSize: 12, color: tokens?.text?.tertiary || '#6b7280' }}>
                    {alert.time || alert.timestamp}
                  </Typography>
                </Box>
              </Box>
            </Box>
          ))
        )}
      </Box>
    </Paper>
  );
};

// ============================================================================
// QUICK ACTIONS - Action buttons grid
// ============================================================================

export const QuickActions = ({
  actions = [],
  columns = 2,
  size = 'md', // 'sm', 'md', 'lg'
  variant = 'card', // 'card', 'button', 'icon'
}) => {
  const tokens = usePremiumTokens();
  const t = useTranslation();

  const sizeStyles = {
    sm: { p: 2, iconSize: 20, fontSize: 12, gap: 1 },
    md: { p: 3, iconSize: 24, fontSize: 14, gap: 1.5 },
    lg: { p: 4, iconSize: 28, fontSize: 16, gap: 2 },
  };

  const styles = sizeStyles[size] || sizeStyles.md;

  const renderCard = (action, index) => (
    <Paper
      key={action.id || index}
      onClick={action.onClick}
      sx={{
        p: styles.p,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: styles.gap,
        bgcolor: tokens?.surface?.secondary || '#1f2937',
        borderRadius: tokens?.radius?.lg || 12,
        border: `1px solid ${tokens?.border?.subtle || '#374151'}`,
        cursor: 'pointer',
        transition: 'all 150ms ease',
        '&:hover': {
          borderColor: tokens?.brand?.primary || '#6366f1',
          bgcolor: tokens?.surface?.hover || alpha('#fff', 0.05),
          transform: 'translateY(-2px)',
        },
      }}
    >
      <Box
        sx={{
          color: action.color || tokens?.brand?.primary || '#6366f1',
          '& svg': { fontSize: styles.iconSize },
        }}
      >
        {action.icon}
      </Box>
      <Typography
        sx={{
          fontSize: styles.fontSize,
          fontWeight: 500,
          color: tokens?.text?.primary || '#f9fafb',
          textAlign: 'center',
        }}
      >
        {action.label}
      </Typography>
    </Paper>
  );

  const renderButton = (action, index) => (
    <Box
      key={action.id || index}
      onClick={action.onClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        p: 2,
        borderRadius: tokens?.radius?.md || 8,
        cursor: 'pointer',
        transition: 'all 150ms ease',
        '&:hover': {
          bgcolor: tokens?.surface?.hover || alpha('#fff', 0.05),
        },
      }}
    >
      <Box
        sx={{
          color: action.color || tokens?.brand?.primary || '#6366f1',
          '& svg': { fontSize: 20 },
        }}
      >
        {action.icon}
      </Box>
      <Typography
        sx={{
          fontSize: 14,
          fontWeight: 500,
          color: tokens?.text?.primary || '#f9fafb',
        }}
      >
        {action.label}
      </Typography>
    </Box>
  );

  if (variant === 'button') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        {actions.map(renderButton)}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: `repeat(${columns}, 1fr)`,
        gap: 2,
      }}
    >
      {actions.map(renderCard)}
    </Box>
  );
};

// ============================================================================
// DASHBOARD WIDGET - Container for widgets
// ============================================================================

export const DashboardWidget = ({
  children,
  title,
  subtitle,
  size = '1x1', // '1x1', '2x1', '1x2', '2x2', 'full'
  loading = false,
  onRefresh,
  onExpand,
  noPadding = false,
  action,
}) => {
  const tokens = usePremiumTokens();
  const t = useTranslation();

  const sizeMap = {
    '1x1': { gridColumn: 'span 1', gridRow: 'span 1' },
    '2x1': { gridColumn: 'span 2', gridRow: 'span 1' },
    '1x2': { gridColumn: 'span 1', gridRow: 'span 2' },
    '2x2': { gridColumn: 'span 2', gridRow: 'span 2' },
    'full': { gridColumn: '1 / -1', gridRow: 'span 1' },
  };

  const gridStyle = sizeMap[size] || sizeMap['1x1'];

  // Cores que funcionam em ambos os modos (dark/light)
  const surfaceBg = tokens?.surface?.elevated || (tokens?.isDark ? '#161b22' : '#f6f8fa');
  const borderSubtle = tokens?.border?.subtle || (tokens?.isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.08)');
  const borderDefault = tokens?.border?.default || (tokens?.isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.15)');
  const textPrimary = tokens?.text?.primary || (tokens?.isDark ? '#f0f6fc' : '#1f2328');
  const textTertiary = tokens?.text?.tertiary || (tokens?.isDark ? '#6e7681' : '#8b949e');

  return (
    <Paper
      sx={{
        ...gridStyle,
        p: noPadding ? 0 : 3,
        bgcolor: surfaceBg,
        borderRadius: tokens?.radius?.lg || 12,
        border: `1px solid ${borderSubtle}`,
        transition: 'all 150ms ease',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        '&:hover': {
          borderColor: borderDefault,
        },
      }}
    >
      {/* Header */}
      {(title || onRefresh || onExpand || action) && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 2,
            px: noPadding ? 3 : 0,
            pt: noPadding ? 3 : 0,
          }}
        >
          <Box>
            {title && (
              <Typography
                sx={{
                  fontSize: 15,
                  fontWeight: 600,
                  color: textPrimary,
                  background: 'transparent',
                }}
              >
                {title}
              </Typography>
            )}
            {subtitle && (
              <Typography
                sx={{
                  fontSize: 12,
                  color: textTertiary,
                  mt: 0.25,
                  background: 'transparent',
                }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {action}
            {onRefresh && (
              <IconButton size="small" onClick={onRefresh} sx={{ color: tokens?.text?.tertiary }}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            )}
            {onExpand && (
              <IconButton size="small" onClick={onExpand} sx={{ color: tokens?.text?.tertiary }}>
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        </Box>
      )}

      {/* Content */}
      <Box sx={{ flex: 1, minHeight: 0, px: noPadding ? 3 : 0, pb: noPadding ? 3 : 0 }}>
        {loading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <Skeleton variant="rectangular" width="100%" height="100%" sx={{ borderRadius: 1 }} />
          </Box>
        ) : (
          children
        )}
      </Box>
    </Paper>
  );
};

// ============================================================================
// DASHBOARD GRID - Responsive grid layout
// ============================================================================

export const DashboardGrid = ({
  children,
  columns = 4,
  gap = 3,
  minRowHeight = 180,
}) => {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: 'repeat(1, 1fr)',
          sm: 'repeat(2, 1fr)',
          md: `repeat(${Math.min(columns, 3)}, 1fr)`,
          lg: `repeat(${columns}, 1fr)`,
        },
        gridAutoRows: `minmax(${minRowHeight}px, auto)`,
        gap,
      }}
    >
      {children}
    </Box>
  );
};

// ============================================================================
// USE DASHBOARD DATA - Hook for dashboard data
// ============================================================================

export const useDashboardData = (options = {}) => {
  const {
    refreshInterval = 30000, // 30 seconds
    autoRefresh = true,
  } = options;

  const [loading, setLoading] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(new Date());

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      // Implement data fetching logic here
      await new Promise((resolve) => setTimeout(resolve, 500));
      setLastRefresh(new Date());
    } finally {
      setLoading(false);
    }
  }, []);

  // Auto refresh
  React.useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(refresh, refreshInterval);
    return () => clearInterval(interval);
  }, [autoRefresh, refreshInterval, refresh]);

  return {
    loading,
    lastRefresh,
    refresh,
  };
};

// ============================================================================
// DEFAULT EXPORT
// ============================================================================

export default StatCard;
